/**
 * SORA Cinema — full-bleed product story (NON-sticky, in-flow).
 *
 * HARD REQUIREMENTS (why this rewrite exists):
 *   • The user MUST always be able to scroll the page straight past this
 *     section. There is NO scroll-jacking, NO sticky pinning, NO tall
 *     wrapper, NO scroll listener, NO wheel/touch/key preventDefault, and
 *     NOTHING ever touches html/body/main overflow. The section is a normal
 *     in-flow block with a finite height — the page scrolls it like any image.
 *   • The chapter buttons (rail) are real <button>s wired to click handlers
 *     that switch chapters instantly, with zero dependency on scroll.
 *   • Cinematic feel is preserved with a pure-CSS crossfade + ken-burns and an
 *     optional gentle autoplay that pauses on hover/focus, when off-screen, or
 *     under prefers-reduced-motion. Interaction beats the pin effect.
 *
 * Video-ready: chapters may carry { video, poster } (Kling / Runway / Luma loops).
 */
(function () {
  var AUTOPLAY_MS = 4200; // hero-ac-speed-fix-20260725 — premium but snappy (was 5200)

  function num(v) {
    var n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  /* Cinema Apple sharpness (2026-08-01): bump Shopify CDN width= to ≥3000
     when a lower cap slipped through Liquid. CDN clamps to uploaded master. */
  function hiResUrl(url) {
    if (!url || typeof url !== "string") return url;
    return url.replace(/([?&]width=)(\d+)/i, function (_m, pre, w) {
      return pre + (Number(w) < 3000 ? "3000" : w);
    });
  }

  function applyCineImage(media, f) {
    var src = hiResUrl(f.image);
    media.src = src;
    if (f.srcset && typeof f.srcset === "string") {
      media.srcset = f.srcset;
    } else if (src && /[?&]width=/i.test(src)) {
      media.srcset =
        src.replace(/([?&]width=)\d+/i, function (_m, pre) { return pre + "1500"; }) +
        " 1500w, " +
        src.replace(/([?&]width=)\d+/i, function (_m, pre) { return pre + "2000"; }) +
        " 2000w, " +
        src.replace(/([?&]width=)\d+/i, function (_m, pre) { return pre + "3000"; }) +
        " 3000w";
    }
    media.sizes = "100vw";
  }

  function init() {
    var root = document.querySelector("[data-sora-cine]");
    if (!root || root.hasAttribute("data-cine-ready")) return;

    var layersWrap = root.querySelector("[data-cine-layers]");
    var railWrap = root.querySelector("[data-cine-rail]");
    var caption = root.querySelector("[data-cine-caption]");
    var capEyebrow = root.querySelector("[data-cine-eyebrow]");
    var capTitle = root.querySelector("[data-cine-title]");
    var capBody = root.querySelector("[data-cine-body]");
    if (!layersWrap) return;

    var features = [];
    var jsonEl = root.querySelector("[data-sora-features]");
    try {
      /* Strip BOM/ZWSP — Windows editors can prefix the Liquid snippet; a leading
         U+FEFF makes JSON.parse throw and leaves the rail empty + "Tap chapters". */
      var raw = ((jsonEl && jsonEl.textContent) || "[]")
        .replace(/^\uFEFF/, "")
        .replace(/[\u200B-\u200D\u2060]/g, "")
        .trim();
      features = JSON.parse(raw || "[]");
    } catch (e) {
      features = [];
    }
    if (!Array.isArray(features)) features = [];
    features = features.filter(function (f) {
      return f && typeof f === "object" && (f.image || f.video);
    });
    if (!features.length) return;

    var count = features.length;
    var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* -------- Build media layers (replaces the no-JS fallback layer) -------- */
    layersWrap.textContent = "";
    var layers = [];
    var videos = [];
    var medias = [];
    features.forEach(function (f, i) {
      var layer = document.createElement("div");
      var kind = f.kind === "packshot" || f.kind === "detail" ? "packshot" : "lifestyle";
      layer.className = "sora-cine__layer sora-cine__layer--" + kind;

      var media;
      if (f.video) {
        media = document.createElement("video");
        media.muted = true;
        media.loop = true;
        media.playsInline = true;
        media.setAttribute("playsinline", "");
        media.setAttribute("muted", "");
        media.preload = i === 0 ? "auto" : "metadata";
        if (f.poster) media.poster = f.poster;
        var src = document.createElement("source");
        src.src = f.video;
        src.type = /\.webm(\?|$)/i.test(f.video) ? "video/webm" : "video/mp4";
        media.appendChild(src);
        videos[i] = media;
      } else {
        media = document.createElement("img");
        applyCineImage(media, f);
        media.alt = "";
        media.decoding = "async";
        media.loading = i === 0 ? "eager" : "lazy";
        if (i === 0) media.fetchPriority = "high";
        media.draggable = false;
      }
      media.className = "sora-cine__media";
      // Packshot-only optional object-position. Lifestyle uses CSS contain + center.
      if (kind === "packshot" && f.focus && typeof f.focus === "string") {
        media.style.objectPosition = f.focus;
      }

      /* True AI outpaint masters (cineWide) — full-bleed cover, NO canvas edge-stretch.
         Owner 2026-08-01: sora-cine__edge-fill smear quarantined. */
      var cineWide = !!(f.cineWide || f.cine_wide);
      if (cineWide) layer.classList.add("sora-cine__layer--cine-wide");
      layer.dataset.cineWide = cineWide ? "1" : "0";

      /* Wrap media so lifestyle chapters can host a light seam feather
         without covering hotspots (overlay is pointer-events: none). */
      var mediaWrap = document.createElement("div");
      mediaWrap.className = "sora-cine__media-wrap";
      mediaWrap.appendChild(media);
      if (kind === "lifestyle" && !cineWide) {
        var vig = document.createElement("div");
        vig.className = "sora-cine__ambient-vignette-overlay";
        vig.setAttribute("aria-hidden", "true");
        mediaWrap.appendChild(vig);
      }
      layer.appendChild(mediaWrap);

      layersWrap.appendChild(layer);
      layers.push(layer);
      medias.push(media);
    });

    /* -------- Build chapter rail (real buttons, click-driven) -------- */
    var ticks = [];
    if (railWrap) {
      features.forEach(function (f, i) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "sora-cine__tick";
        b.setAttribute("aria-label", "Show: " + (f.title || "Chapter " + (i + 1)));
        var dot = document.createElement("span");
        dot.className = "sora-cine__tick-dot";
        var lab = document.createElement("span");
        lab.className = "sora-cine__tick-label";
        lab.textContent = f.eyebrow || "Chapter " + (i + 1);
        b.appendChild(dot);
        b.appendChild(lab);
        b.addEventListener("click", function () {
          userGoTo(i);
        });
        railWrap.appendChild(b);
        ticks.push(b);
      });
    }

    /* -------- Shoppable markers -------------------------------------------
       The marker is anchored in IMAGE space, never stage space. The painted
       rectangle of the photograph moves and resizes with every viewport, so a
       fixed percentage of the section would drift off the unit exactly when the
       framing changes. Anchor precedence: the chapter's own hand-set x/y, then
       the image's Shopify focal point, then a per-category fallback.

       Nothing renders at all unless the product is genuinely purchasable, and
       "add to bag" clicks the real buy-box button rather than posting to
       /cart/add itself — that keeps variant selection, the F-Gas installer
       capture and the express-checkout interception in one place. */
    var stage = root.querySelector("[data-cine-stage]");

    function readBuy() {
      var el = document.getElementById("sora-product-bridge");
      var data = null;
      try {
        data = JSON.parse((el && el.textContent) || "null");
      } catch (e) {
        data = null;
      }
      if (!data || data.available !== true) return null;
      var addBtn = document.querySelector('#sora-product-form button[name="add"]');
      if (!addBtn || addBtn.disabled) return null;
      var price = root.getAttribute("data-cine-price") || "";
      if (!price && typeof data.priceAmount === "number" && data.currency) {
        try {
          price = new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: data.currency,
          }).format(data.priceAmount / 100);
        } catch (e) {
          price = "";
        }
      }
      return {
        title: data.title || root.getAttribute("data-product-title") || "",
        price: price,
        addBtn: addBtn,
      };
    }

    var buy = readBuy();
    var spots = [];
    var openSpot = null;

    function anchorFor(f) {
      var ax = num(f.x);
      var ay = num(f.y);
      if (ax !== null && ay !== null) return [ax, ay];
      if (typeof f.focus === "string") {
        var parts = f.focus.split(/\s+/);
        var fx = parseFloat(parts[0]);
        var fy = parseFloat(parts[1]);
        if (Number.isFinite(fx) && Number.isFinite(fy)) return [fx, fy];
      }
      var sx = num(f.sx);
      var sy = num(f.sy);
      if (sx !== null && sy !== null) return [sx, sy];
      return [50, 50];
    }

    function buildSpot(i, f) {
      var wrap = document.createElement("div");
      wrap.className = "sora-cine__spots";

      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "sora-cine__spot";
      btn.id = "sora-cine-spot-btn-" + i;
      btn.setAttribute("aria-expanded", "false");
      btn.setAttribute("aria-controls", "sora-cine-spot-card-" + i);
      btn.setAttribute(
        "aria-label",
        "Shop " + buy.title + (buy.price ? ", " + buy.price : "")
      );
      var pin = document.createElement("span");
      pin.className = "sora-cine__spot-pin";
      pin.setAttribute("aria-hidden", "true");
      btn.appendChild(pin);

      var card = document.createElement("div");
      card.className = "sora-cine__spot-card";
      card.id = "sora-cine-spot-card-" + i;
      card.hidden = true;

      var name = document.createElement("p");
      name.className = "sora-cine__spot-name";
      name.textContent = buy.title;
      card.appendChild(name);

      if (buy.price) {
        var price = document.createElement("p");
        price.className = "sora-cine__spot-price";
        price.textContent = buy.price;
        card.appendChild(price);
      }

      var actions = document.createElement("div");
      actions.className = "sora-cine__spot-actions";
      var add = document.createElement("button");
      add.type = "button";
      add.className = "sora-cine__spot-add";
      add.textContent = "Add to bag";
      add.addEventListener("click", function () {
        closeSpot();
        buy.addBtn.click();
      });
      var more = document.createElement("a");
      more.className = "sora-cine__spot-more";
      more.href = "#product-root";
      more.textContent = "Details";
      more.addEventListener("click", function () {
        closeSpot();
      });
      actions.appendChild(add);
      actions.appendChild(more);
      card.appendChild(actions);

      btn.addEventListener("click", function () {
        if (openSpot === spot) closeSpot();
        else showSpot(spot);
      });

      wrap.appendChild(btn);
      wrap.appendChild(card);
      layers[i].appendChild(wrap);

      var spot = { wrap: wrap, btn: btn, card: card, index: i, feature: f };
      return spot;
    }

    function showSpot(spot) {
      if (openSpot && openSpot !== spot) closeSpot();
      openSpot = spot;
      spot.card.hidden = false;
      spot.btn.setAttribute("aria-expanded", "true");
      root.classList.add("is-spot-open");
      // The card has real dimensions only once it is shown, and placement
      // depends on them.
      layoutSpots();
      syncPaused();
    }

    function closeSpot() {
      if (!openSpot) return;
      openSpot.card.hidden = true;
      openSpot.btn.setAttribute("aria-expanded", "false");
      openSpot = null;
      root.classList.remove("is-spot-open");
      syncPaused();
    }

    /* Painted rectangle of a contained image, in the layer's own untransformed
       coordinates. Read from the element's real box and object-position so it
       stays correct whatever the stylesheet does at this breakpoint. */
    function paintedRect(media) {
      var layer = media.closest ? media.closest(".sora-cine__layer") : null;
      /* Lifestyle frames are sized to the photo; media fills the wrap via cover. */
      if (
        layer &&
        layer.classList.contains("sora-cine__layer--lifestyle") &&
        media.getBoundingClientRect &&
        layer.getBoundingClientRect
      ) {
        var mr = media.getBoundingClientRect();
        var lr = layer.getBoundingClientRect();
        if (mr.width && mr.height) {
          return {
            left: mr.left - lr.left,
            top: mr.top - lr.top,
            width: mr.width,
            height: mr.height,
          };
        }
      }
      var nw = media.naturalWidth || media.videoWidth || 0;
      var nh = media.naturalHeight || media.videoHeight || 0;
      var bw = media.offsetWidth;
      var bh = media.offsetHeight;
      if (!nw || !nh || !bw || !bh) return null;
      var cs = window.getComputedStyle(media);
      var pl = parseFloat(cs.paddingLeft) || 0;
      var pr = parseFloat(cs.paddingRight) || 0;
      var pt = parseFloat(cs.paddingTop) || 0;
      var pb = parseFloat(cs.paddingBottom) || 0;
      var cw = Math.max(0, bw - pl - pr);
      var ch = Math.max(0, bh - pt - pb);
      if (!cw || !ch) return null;
      var contain = cs.objectFit !== "cover";
      var scale = contain
        ? Math.min(cw / nw, ch / nh)
        : Math.max(cw / nw, ch / nh);
      var pw = nw * scale;
      var ph = nh * scale;
      var op = (cs.objectPosition || "50% 50%").trim().split(/\s+/);
      var ox = offsetFor(op[0], cw - pw);
      var oy = offsetFor(op.length > 1 ? op[1] : "50%", ch - ph);
      return { left: pl + ox, top: pt + oy, width: pw, height: ph };
    }

    function offsetFor(token, slack) {
      if (!token) return slack * 0.5;
      if (token.slice(-1) === "%") return (slack * parseFloat(token)) / 100;
      var px = parseFloat(token);
      return Number.isFinite(px) ? px : slack * 0.5;
    }

    /* Layout frames. Edge-stretch canvas fill REMOVED (owner 2026-08-01 smear redline).
       cineWide masters → full-bleed cover. Packshot → soft studio CSS + contain.
       Non-wide lifestyle → cover (no letterbox gutters / no smear). */
    function layoutLifestyleFrames() {
      for (var i = 0; i < layers.length; i++) {
        var layer = layers[i];
        if (!layer) continue;
        var isLife = layer.classList.contains("sora-cine__layer--lifestyle");
        var isPack = layer.classList.contains("sora-cine__layer--packshot");
        if (!isLife && !isPack) continue;
        var wrap = layer.querySelector(".sora-cine__media-wrap");
        var media = medias[i];
        if (!wrap || !media) continue;
        var cineWide = layer.dataset.cineWide === "1";
        var lw = layer.clientWidth;
        var lh = layer.clientHeight;
        if (!lw || !lh) continue;

        /* Kill any legacy edge-fill canvases left in DOM */
        var legacyFill = layer.querySelector(".sora-cine__edge-fill");
        if (legacyFill) legacyFill.remove();
        layer.classList.remove("has-edge-fill", "has-plate-fallback");

        wrap.style.inset = "0";
        wrap.style.left = "";
        wrap.style.top = "";
        wrap.style.width = "";
        wrap.style.height = "";
        media.style.width = "100%";
        media.style.height = "100%";
        media.style.inset = "0";
        media.style.backgroundColor = "transparent";

        if (cineWide || isLife) {
          /* Wide outpaint or lifestyle: edge-to-edge cover — no gutters */
          media.style.objectFit = "cover";
          media.style.objectPosition =
            (features[i] && features[i].focus) || (isLife ? "center 32%" : "center");
          media.style.padding = "0";
          continue;
        }

        if (isPack) {
          /* Soft studio stage CSS fills L/R — never stretch product edges */
          media.style.objectFit = "contain";
          media.style.objectPosition = "center";
          media.style.padding = "clamp(1.5rem, 4vw, 3rem) clamp(1.5rem, 5vw, 4.5rem)";
        }
      }
    }

    function layoutSpots() {
      layoutLifestyleFrames();
      if (!spots.length) return;
      var capRect = null;
      if (caption && stage) {
        var cr = caption.getBoundingClientRect();
        var sr = stage.getBoundingClientRect();
        if (cr.width && cr.height) {
          capRect = {
            left: cr.left - sr.left,
            right: cr.right - sr.left,
            top: cr.top - sr.top,
            bottom: cr.bottom - sr.top,
          };
        }
      }
      for (var i = 0; i < spots.length; i++) {
        var spot = spots[i];
        if (!spot) continue;
        var rect = paintedRect(medias[i]);
        if (!rect) {
          spot.wrap.hidden = true;
          continue;
        }
        var a = anchorFor(spot.feature);
        var inset = 26;
        var x = rect.left + (rect.width * a[0]) / 100;
        var y = rect.top + (rect.height * a[1]) / 100;
        x = clamp(x, rect.left + inset, rect.left + rect.width - inset);
        y = clamp(y, rect.top + inset, rect.top + rect.height - inset);

        // Never sit on the headline: if the anchor lands in the caption block,
        // lift it just clear of the text rather than covering it.
        if (capRect && x < capRect.right + 14 && y > capRect.top - 14 && y < capRect.bottom + 14) {
          var lifted = capRect.top - 20;
          if (lifted > rect.top + inset) y = lifted;
          else x = Math.min(capRect.right + 28, rect.left + rect.width - inset);
        }

        var w = spot.wrap.offsetWidth || (stage ? stage.offsetWidth : 0);
        var h = spot.wrap.offsetHeight || (stage ? stage.offsetHeight : 0);
        spot.wrap.hidden = false;
        spot.btn.style.left = x + "px";
        spot.btn.style.top = y + "px";

        // Prefer to open the card in the letterbox margin beside the photograph
        // rather than on top of the unit. On a wide stage that margin is large
        // and empty; when it is not (phones), fall back to a tooltip beside the
        // marker, and the stylesheet turns that into a bottom sheet.
        var cardW = spot.card.offsetWidth || 248;
        var cardH = spot.card.offsetHeight || 132;
        var gutterL = rect.left;
        var gutterR = w - (rect.left + rect.width);
        var need = cardW + 32;
        var side = x > w * 0.62 ? "left" : "right";
        var vpos = y > h * 0.55 ? "above" : "below";
        if (gutterR >= need || gutterL >= need) {
          var useRight = gutterR >= gutterL;
          spot.card.setAttribute("data-place", "gutter");
          spot.card.style.left =
            (useRight ? rect.left + rect.width + 24 : rect.left - 24 - cardW) + "px";
          spot.card.style.top = clamp(y - cardH / 2, 16, Math.max(16, h - cardH - 16)) + "px";
        } else {
          spot.card.removeAttribute("data-place");
          spot.card.style.left = x + "px";
          spot.card.style.top = y + "px";
        }
        spot.btn.setAttribute("data-side", side);
        spot.btn.setAttribute("data-vpos", vpos);
        spot.card.setAttribute("data-side", side);
        spot.card.setAttribute("data-vpos", vpos);
      }
    }

    function clamp(v, lo, hi) {
      if (hi < lo) return (lo + hi) / 2;
      return v < lo ? lo : v > hi ? hi : v;
    }

    if (buy) {
      features.forEach(function (f, i) {
        spots[i] = buildSpot(i, f);
      });
      document.addEventListener("keydown", function (ev) {
        if (ev.key === "Escape" && openSpot) {
          var btn = openSpot.btn;
          closeSpot();
          btn.focus();
        }
      });
      document.addEventListener("click", function (ev) {
        if (!openSpot) return;
        if (openSpot.wrap.contains(ev.target)) return;
        closeSpot();
      });
    }

    /* Always layout lifestyle vignettes (and spots when present) — not gated on buy. */
    medias.forEach(function (m) {
      if (m.tagName === "IMG") {
        if (m.complete) window.requestAnimationFrame(layoutSpots);
        else m.addEventListener("load", layoutSpots);
      } else {
        m.addEventListener("loadedmetadata", layoutSpots);
      }
    });
    if ("ResizeObserver" in window && stage) {
      new ResizeObserver(function () {
        layoutSpots();
      }).observe(stage);
    } else {
      window.addEventListener("resize", layoutSpots);
    }

    var current = -1;

    function paintCaption(f) {
      if (caption) caption.classList.remove("is-in");
      if (capEyebrow) capEyebrow.textContent = f.eyebrow || "";
      if (capTitle) capTitle.textContent = f.title || "";
      if (capBody) capBody.textContent = f.body || "";
      if (caption) {
        window.requestAnimationFrame(function () {
          caption.classList.add("is-in");
        });
      }
    }

    function setActive(i) {
      if (i < 0) i = 0;
      if (i >= count) i = count - 1;
      if (i === current) return;
      closeSpot();
      current = i;
      var f = features[i];
      var activeKind =
        f && (f.kind === "packshot" || f.kind === "detail") ? "packshot" : "lifestyle";
      root.classList.toggle("is-packshot-chapter", activeKind === "packshot");
      root.classList.toggle("is-lifestyle-chapter", activeKind !== "packshot");
      for (var li = 0; li < layers.length; li++) {
        var on = li === i;
        layers[li].classList.toggle("is-active", on);
        var vid = videos[li];
        if (vid) {
          if (on) {
            var p = vid.play();
            if (p && p.catch) p.catch(function () {});
          } else {
            vid.pause();
          }
        }
      }
      for (var ti = 0; ti < ticks.length; ti++) {
        ticks[ti].classList.toggle("is-active", ti === i);
        ticks[ti].setAttribute("aria-current", ti === i ? "true" : "false");
      }
      paintCaption(f);
      // The caption changes height per chapter, so the keep-clear-of-headline
      // test has to run again for the chapter now on screen.
      window.requestAnimationFrame(layoutSpots);
    }

    /* -------- Autoplay (gentle, always pausable, never blocks the page) ---- */
    var timer = null;
    var paused = false;
    var offscreen = false;
    var heldByUser = false;

    function advance() {
      setActive((current + 1) % count);
    }

    function tickAutoplay() {
      if (count < 2 || reduceMotion) return;
      if (paused || offscreen) return;
      advance();
    }

    function startAutoplay() {
      if (timer || count < 2 || reduceMotion) return;
      timer = window.setInterval(tickAutoplay, AUTOPLAY_MS);
    }

    // A user click switches chapter immediately and briefly holds autoplay so
    // it doesn't yank away from what they just chose.
    var resumeT = null;
    function userGoTo(i) {
      setActive(i);
      heldByUser = true;
      syncPaused();
      root.classList.add("is-cue-hidden");
      if (resumeT) window.clearTimeout(resumeT);
      resumeT = window.setTimeout(function () {
        heldByUser = false;
        syncPaused();
      }, AUTOPLAY_MS * 1.6);
    }

    // Pause while the pointer/focus is on the stage — respects interaction.
    var hovering = false;
    var tabHidden = false;
    function syncPaused() {
      paused = hovering || tabHidden || heldByUser || openSpot !== null;
    }
    root.addEventListener("mouseenter", function () { hovering = true; syncPaused(); });
    root.addEventListener("mouseleave", function () { hovering = false; syncPaused(); });
    root.addEventListener("focusin", function () { hovering = true; syncPaused(); });
    root.addEventListener("focusout", function () { hovering = false; syncPaused(); });
    document.addEventListener("visibilitychange", function () {
      tabHidden = document.hidden;
      syncPaused();
    });

    root.setAttribute("data-cine-ready", "");
    function markHires() {
      /* Chromium can under-report naturalWidth vs decoded JPEG (seen 1152 on a
         2400 master). Trust CDN width= / srcset candidates too. */
      var best = 0;
      for (var hi = 0; hi < medias.length; hi++) {
        var m = medias[hi];
        if (!m || m.tagName !== "IMG") continue;
        best = Math.max(best, m.naturalWidth || 0);
        var src = m.currentSrc || m.src || "";
        var wm = src.match(/[?&]width=(\d+)/i);
        if (wm) best = Math.max(best, Number(wm[1]) || 0);
        var ss = m.srcset || "";
        var parts = ss.match(/width=(\d+)/gi) || [];
        for (var pi = 0; pi < parts.length; pi++) {
          var n = Number(String(parts[pi]).replace(/\D/g, ""));
          if (n) best = Math.max(best, n);
        }
      }
      if (best >= 2000) root.setAttribute("data-cine-hires", "1");
      else root.removeAttribute("data-cine-hires");
    }
    medias.forEach(function (m) {
      if (m && m.tagName === "IMG") {
        if (m.complete) markHires();
        else m.addEventListener("load", markHires);
      }
    });
    setActive(0);

    /* -------- Only run autoplay while on-screen (perf, no scroll hook) ----- */
    if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(
        function (entries) {
          for (var k = 0; k < entries.length; k++) {
            offscreen = !entries[k].isIntersecting;
            if (entries[k].isIntersecting) {
              root.classList.add("is-inview");
              startAutoplay();
            } else {
              root.classList.remove("is-inview");
            }
          }
        },
        { rootMargin: "0px 0px 0px 0px", threshold: 0.15 }
      );
      io.observe(root);
    } else {
      root.classList.add("is-inview");
      startAutoplay();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
